import { InspectorControls } from "@wordpress/block-editor";
import { registerBlockStyle } from "@wordpress/blocks";
import { AlignmentMatrixControl, PanelBody } from "@wordpress/components";
import { createHigherOrderComponent } from "@wordpress/compose";
import { addFilter } from "@wordpress/hooks";
import { clsx } from "clsx";

import "./index.scss";

// "className":"is-style-simple-history-image-screenshot"

/**
 * Image screenshot is an extension of the build in core image block.
 
 * It works by combining a couple of things:
 *
 * 1. Register a block variation of the core/image block. 
 *    The varation makes a "new" block show up in the block inserter,
 *    but it's actually just a variation of the core/image block with some
 *    attributes set.
 * 
 * 2. Add a new block style to the core/image block.
 *    This is done by using the `blocks.registerBlockStyle` filter.
 *    The new style is used to style the image block in a way that makes it
 *    look like a screenshot.
 * 
 * 3. Register a new attribute on the core/image block.
 *    This is done by using the `blocks.registerBlockType` filter.
 *    The new attribute is called `simple_history_screenshot_alignment`.
 */

wp.blocks.registerBlockVariation("core/image", {
  name: "simple-history-image-creenshot",
  title: "Screenshot image",
  attributes: {
    className: "is-style-simple-history-image-screenshot",
  },
});

registerBlockStyle("core/image", {
  name: "simple-history-image-screenshot",
  label: "Screenshot (new!)",
});

function modifyImageBlockSettings(settings, name) {
  // Only act on the core/image block.
  if (name !== "core/image") {
    return settings;
  }

  return {
    ...settings,
    attributes: {
      ...settings.attributes,
      simple_history_screenshot_alignment: {
        type: "string",
        default: "center center",
      },
    },
  };
}

const withMyPluginControls = createHigherOrderComponent((BlockEdit) => {
  return (props) => {
    const { name, isSelected, setAttributes, attributes } = props;

    // const [alignment, setAlignment] = useState("center center");

    // Only act on core image block.
    if (name !== "core/image") {
      return <BlockEdit {...props} />;
    }

    const { simple_history_screenshot_alignment } = attributes;

    return (
      <>
        <InspectorControls group="styles">
          <PanelBody title="Screenshot">
            <p>Set alignment and stuff for the screenshot.</p>
            <AlignmentMatrixControl
              value={simple_history_screenshot_alignment}
              onChange={
                (newAlignment) =>
                  setAttributes({
                    simple_history_screenshot_alignment: newAlignment,
                  })
                // setAlignment(newAlignment)
              }
            />
          </PanelBody>
        </InspectorControls>

        <BlockEdit {...props} />
      </>
    );
  };
}, "withMyPluginControls");

function addCustomClassesUsingGetSaveContentExtraProps(
  extraProps,
  blockType,
  attributes
) {
  const hasScreenshotStyle = extraProps?.className?.includes(
    "is-style-simple-history-image-screenshot"
  );

  // Check if it's your specific block type
  if (blockType.name === "core/image") {
    // Only act if extraProps.className contains ".is-style-simple-history-image-screenshot".
    if (hasScreenshotStyle) {
      // Add your classes to existing ones
      // attributes.simple_history_screenshot_alignment
      const alignmentClassName =
        "simple_history_screenshot_alignment-" +
        attributes?.simple_history_screenshot_alignment?.replace(" ", "-");

      extraProps.className = clsx(
        extraProps.className,
        "my-custom-class-from-add-custom-classes",
        alignmentClassName
      );
    }
  }

  return extraProps;
}

function modifyImageClasses(element, blockType, attributes) {
  if (blockType.name === "core/image") {
    // Create new className for the wrapper
    const newClassName = clsx(
      element.props.className, // preserve existing classes
      "my-custom-image-class-from-get-save-element",
      {
        "is-special": attributes.someCondition,
      }
    );

    return React.cloneElement(element, {
      className: newClassName,
    });
  }
  return element;
}

/**
 * addAdditionalPropertiesInEditor
 */
const addAdditionalPropertiesInEditor = createHigherOrderComponent(
  (BlockList) => {
    return (props) => {
      const { name, attributes, className } = props;
      const { simple_history_screenshot_alignment } = attributes;

      // return early from the block modification.
      if (name !== "core/image") {
        return <BlockList {...props} />;
      }

      const alignmentClassName =
        "simple_history_screenshot_alignment-" +
        attributes?.simple_history_screenshot_alignment?.replace(" ", "-");

      const newClassName = clsx(className, alignmentClassName);

      return <BlockList {...props} className={newClassName} />;
    };
  },
  "addAdditionalPropertiesInEditor"
);

addFilter(
  "editor.BlockListBlock",
  "namespace/addAdditionalPropertiesInEditor",
  addAdditionalPropertiesInEditor
);

addFilter(
  "editor.BlockEdit",
  "my-plugin/with-inspector-controls",
  withMyPluginControls
);

addFilter(
  "blocks.registerBlockType",
  "my-plugin/class-names/list-block",
  modifyImageBlockSettings
);

addFilter(
  "blocks.getSaveContent.extraProps",
  "my-plugin/add-custom-classes",
  addCustomClassesUsingGetSaveContentExtraProps
);

// addFilter(
//   "blocks.getSaveElement",
//   "my-plugin/modify-image-classes",
//   modifyImageClasses
// );
